import {
  ProjectHttpClient,
  ContentServiceHttpClient,
} from '../../common/services/httpClient';
import { getNarrativesForKfas } from '../narratives/narrativesService';

const sortByNewestDate = (hypotheses) =>
  hypotheses.sort((hypothesisA, hypothesisB) => {
    const dateA = new Date(hypothesisA.dateModified);
    const dateB = new Date(hypothesisB.dateModified);
    return dateB - dateA;
  });

const sortBySortOrder = (hypotheses) =>
  hypotheses.sort(
    (hypothesisA, hypothesisB) => hypothesisA.sortOrder - hypothesisB.sortOrder
  );

const baseServicePath = (projectId, hypothesisThreadId, versionStatus) => {
  const pathForId = hypothesisThreadId ? `/${hypothesisThreadId}` : '';
  const pathForStatus = versionStatus ? `/${versionStatus}` : '';

  return `projects/${projectId}/hypotheses${pathForId}${pathForStatus}`;
};

export const getHypotheses = async (projectId) => {
  const response = await ProjectHttpClient.get(baseServicePath(projectId));
  return sortBySortOrder(response.data);
};

export const getKFAs = async (projectId) => {
  const response = await ContentServiceHttpClient.get(
    `projects/${projectId}/kfa/list`
  );
  return response.data && response.data.items
    ? sortBySortOrder(response.data.items)
    : [];
};

export const getHypothesesWithConnectedData = async (projectId) => {
  const kfas = await getHypotheses(projectId);
  const kfasWithConnectedContentData = await getNarrativesForKfas(projectId);

  const emptyConnectedContent = {
    connectedKfas: [],
    connectedInsights: [],
    connectedAnalyses: [],
  };
  return kfas.map((kfa) => {
    const connectedContent =
      kfasWithConnectedContentData.find(
        (kfaWithConnectedContentData) =>
          kfaWithConnectedContentData.contentThreadId === kfa.contentThreadId
      ) || emptyConnectedContent;

    return {
      ...kfa,
      ...connectedContent,
    };
  });
};

export const getSharedHypotheses = async (projectId) => {
  const response = await ProjectHttpClient.get(
    `projects/${projectId}/hypotheses?onlyShared=true`
  );
  return sortByNewestDate(response.data);
};

export const getHypothesis = async (projectId, hypothesisId) => {
  const response = await ContentServiceHttpClient.get(
    `/projects/${projectId}/kfa/detail/${hypothesisId}`
  );
  return response.data;
};

export const postHypothesis = async (projectId, hypothesis) => {
  const response = await ContentServiceHttpClient.post(
    `/projects/${projectId}/kfa`,
    hypothesis
  );
  return response.data;
};

export const putHypothesis = async (projectId, hypothesis) => {
  const response = await ContentServiceHttpClient.put(
    `/projects/${projectId}/kfa/${hypothesis.contentId}`,
    hypothesis
  );

  return response.data;
};

export const patchHypothesisBody = async (
  projectId,
  hypothesisThreadId,
  versionStatus,
  hypothesisBody
) => {
  const response = await ProjectHttpClient.patch(
    baseServicePath(projectId, hypothesisThreadId, versionStatus),
    { body: hypothesisBody }
  );
  return response.data;
};

export const deleteHypothesis = async (projectId, hypothesisThreadId) => {
  const response = await ContentServiceHttpClient.delete(
    `/projects/${projectId}/contents/${hypothesisThreadId}`
  );
  return response.data;
};

export const getHypothesisVersions = async (projectId, hypothesisThreadId) => {
  const response = await ContentServiceHttpClient.get(
    `/projects/${projectId}/contents/${hypothesisThreadId}/versions`
  );
  return response.data;
};

export const updateHypothesesOrder = async (
  projectId,
  orderedContentThreadIds
) => {
  const response = await ProjectHttpClient.post(
    `${baseServicePath(projectId)}/sortorder`,
    orderedContentThreadIds
  );

  return response.data;
};

export const updateKFAOrder = async (projectId, sortChangeArray) => {
  const response = await ContentServiceHttpClient.post(
    `/projects/${projectId}/kfa/sortorder/v2`,
    sortChangeArray
  );

  return response.data;
};
